import {Injectable} from '@angular/core';
import {ApiDataService} from '../sp-api/sp-api-data/api-data.service';
import {Project} from '../../models/project';
import {CrawlStatus} from '../../models/crawl-status';
import {Subject} from 'rxjs';
import {Audience} from '../../models/audience';
import {LoggerService} from '../sp-logger/logger.service';
import {AlertService} from '../sp-alert/alert.service';
import { ActiveToast } from 'ngx-toastr';
import { FeatureFlagsService } from '../feature-flag.service';
import { DiReportNavigatorService } from '../sp-navigation/di-report-navigator.service';

@Injectable({
  providedIn: 'root'
})
export class CrawlTrackerService {
  public static readonly STEP_CRITERIA_AVAILABLE = 2;
  public static readonly STEP_PERSONAE = 3;

  private waitingRoom: Project[] = [];
  personaeWaitingRoom: Audience[] = [];
  private intervals: Map<number, any> = new Map();
  private personaIntervals: Map<number, any> = new Map();
  private binaryTracked: Set<number> = new Set();
  private pendingAdds: Set<number> = new Set();

  crawlDoneNotifier: Subject<Project> = new Subject<Project>();
  crawlCriteriaDoneNotifier: Subject<Project> = new Subject<Project>();
  projectCleanNotifier: Subject<Project> = new Subject<Project>();
  personaCrawlDoneNotifier: Subject<[Project, Audience]> = new Subject<[Project, Audience]>();

  project;

  constructor(
    private apiData: ApiDataService,
    private logger: LoggerService,
    private alert: AlertService,
    private diReportNavigator: DiReportNavigatorService,
    private featureFlag: FeatureFlagsService,
  ) {
    this.setUpNotificationsSubscriptions();
  }

  setUpNotificationsSubscriptions() {
    this.crawlDoneNotifier.subscribe(project => {
      this.setUpToasterNotification(project);
    });

    this.personaCrawlDoneNotifier.subscribe(([project, persona]) => {
      this.setUpToasterNotification(project, persona);
    });
  }

  clearAll() {
    this.waitingRoom = [];
    this.personaeWaitingRoom = [];
    this.binaryTracked = new Set();
    this.pendingAdds = new Set();
    Array.from(this.intervals.values()).forEach(interval => {
      clearInterval(interval);
    });
    Array.from(this.personaIntervals.values()).forEach(interval => {
      clearInterval(interval);
    });
    this.intervals = new Map();
    this.personaIntervals = new Map();
  }

  async addPersona(audience: Audience, watchInterval: number = 10000,project?: Project) {
    if (!this.inPersonaeWaitingRoom(audience)) {
      this.logger.logInfo('[TRACKER] Adding ' + audience.name + ' to persona tracking waiting room');
      let target: Audience = audience;
      let bench: Audience = audience.benchmark;

      let targetCrawling: boolean = await this.getAndAssignAudienceCrawlStatus(target);
      let benchCrawling: boolean = await this.getAndAssignAudienceCrawlStatus(bench);

      if (!bench) {
        this.logger.logError('[TRACKER] Adding persona ' + audience.name + ' with no defined benchmark, are you sure it\'s a persona ?');
      } else if (targetCrawling || benchCrawling) {
        this.personaeWaitingRoom.push(target);
        this.startWatchingPersona(project, target, watchInterval);
      }
    }
    return audience;
  }

  async addProject(project: Project, watchInterval: number = 10000) {
    if (project.id == 0) {
      project.audience_target.status = CrawlStatus.done();
      project.audience_bench.status = CrawlStatus.done();
      return;
    }

    if (this.inWaitingRoom(project) || this.pendingAdds.has(project.id)) return;
    this.pendingAdds.add(project.id);

    try {
      const newCrawlEnabled = await this.featureFlag.isEnabled('new_crawl');
      if (newCrawlEnabled) {
        const diStatus = await this.apiData.getProjectCrawlStatus(project.id);
        if (diStatus !== 'unknown') {
          this.binaryTracked.add(project.id);
          if (diStatus === 'done') {
            this.markAudiencesCrawlDone(project);
            project._ready = true;
            this.projectCleanNotifier.next(project);
          } else {
            this.logger.logInfo('[TRACKER] Adding ' + project.name + ' (ID: ' + project.id + ') to new-crawler project tracking');
            this.waitingRoom.push(project);
            this.startWatchingProject(project, watchInterval);
          }
          return;
        }
      }

      this.binaryTracked.delete(project.id);
      const target = project.audience_target;
      const bench = project.audience_bench;

      const targetCrawling = await this.getAndAssignAudienceCrawlStatus(target);
      const benchCrawling = await this.getAndAssignAudienceCrawlStatus(bench);

      if (targetCrawling || benchCrawling) {
        this.logger.logInfo('[TRACKER] Adding ' + project.name + ' (ID: ' + project.id + ') to project tracking waiting room');
        this.binaryTracked.add(project.id);
        this.waitingRoom.push(project);
        this.startWatching(project, watchInterval);
      } else {
        project._ready = true;
        this.projectCleanNotifier.next(project);
      }
    } finally {
      this.pendingAdds.delete(project.id);
    }
  }

  private markAudiencesCrawlDone(project: Project) {
    const target = project.audience_target;
    const bench = project.audience_bench;
    target.status = CrawlStatus.done();
    bench.status = CrawlStatus.done();
    if (target.latest_crawl) {
      target.latest_crawl.criteria_ready = true;
      target.latest_crawl.segment_ready = true;
    }
    if (bench.latest_crawl) {
      bench.latest_crawl.criteria_ready = true;
      bench.latest_crawl.segment_ready = true;
    }
  }

  isBinaryTracked(project: Project): boolean {
    return this.binaryTracked.has(project.id);
  }

  private startWatchingProject(project: Project, interval: number) {
    const id = setInterval(async () => {
      const status = await this.apiData.getProjectCrawlStatus(project.id);

      if (status === 'done') {
        clearInterval(this.intervals.get(project.id));
        this.waitingRoom = this.waitingRoom.filter(p => p.id !== project.id);
        this.binaryTracked.delete(project.id);
        this.markAudiencesCrawlDone(project);
        project._ready = true;
        this.crawlDoneNotifier.next(project);
      }
    }, interval);

    this.intervals.set(project.id, id);
  }

  private async getAndAssignAudienceCrawlStatus(audience: Audience): Promise<boolean> {
    if (!audience) return false;

    try {
      await this.refreshAudienceStatus(audience);
      return audience.status.state !== 'done';
    } catch (error) {
      if (error.status == 404) {
        this.markAudienceCrawlDone(audience);
      } else {
        audience.status = CrawlStatus.unavailable();
      }
      return false;
    }
  }

  private async refreshAudienceStatus(audience: Audience): Promise<void> {
    const crawl = await this.apiData.getAudienceLatestCrawl(audience);
    if (crawl) audience.latest_crawl = crawl;
    audience.status = CrawlStatus.fromCrawl(crawl);
  }

  private async pollAudienceStatus(audience: Audience): Promise<void> {
    try {
      await this.refreshAudienceStatus(audience);
    } catch (error) {
      if (error.status == 404) {
        this.markAudienceCrawlDone(audience);
      } else {
        this.logger.logWarning('[TRACKER] Could not refresh crawl status of audience ' + audience.id + ', retrying on next tick');
      }
    }
  }

  private markAudienceCrawlDone(audience: Audience) {
    audience.status = CrawlStatus.done();
    if (audience.latest_crawl) {
      audience.latest_crawl.criteria_ready = true;
      audience.latest_crawl.segment_ready = true;
    }
  }

  setUpToasterNotification(project: Project, persona?: Audience) {
    const message = persona
      ? 'Your persona: ' + persona.name + ' is ready, <u>click here</u> to see it'
      : 'Your project: ' + project.name + ' has finished crawling, <u>click here</u> to see it';
    const toast: ActiveToast<any> = this.alert.notify('Success', message, 'success', {timeOut: 10000, enableHtml: true});
    toast.onTap?.subscribe(() => {
      this.diReportNavigator.openDigitalIntelligenceSummary(project.id);
    });
  }

  startWatchingPersona(project: Project, persona: Audience, interval: number) {
    const id = setInterval(async () => {
      const target = persona;
      const bench = persona.benchmark;

      await this.pollAudienceStatus(target);
      await this.pollAudienceStatus(bench);

      if (target.status?.state == 'done' && bench.status?.state == 'done') {
        clearInterval(this.personaIntervals.get(persona.id));
        this.personaeWaitingRoom = this.personaeWaitingRoom.filter(p => p.id !== target.id);
        this.personaCrawlDoneNotifier.next([project, persona]);
      }
    }, interval);

    this.personaIntervals.set(persona.id, id);
  }

  private startWatching(project: Project, interval: number) {
    const id = setInterval(async () => {
      const target = project.audience_target;
      const bench = project.audience_bench;

      await this.pollAudienceStatus(target);
      await this.pollAudienceStatus(bench);

      if (target.status?.state == 'done' && bench.status?.state == 'done') {
        clearInterval(this.intervals.get(project.id));
        this.waitingRoom = this.waitingRoom.filter(p => p.id !== project.id);
        this.binaryTracked.delete(project.id);
        this.markAudiencesCrawlDone(project);
        this.crawlDoneNotifier.next(project);
      } else if (!project._ready && target.latest_crawl?.criteria_ready && bench.latest_crawl?.criteria_ready) {
        this.crawlCriteriaDoneNotifier.next(project);
        project._ready = true;
      }
    }, interval);

    this.intervals.set(project.id, id);
  }

  inWaitingRoom(project: Project): boolean {
    return !!(this.waitingRoom.find(w => w.id == project.id));
  }

  inPersonaeWaitingRoom(audience: Audience): boolean {
    return !!(this.personaeWaitingRoom.find(a => a.id == audience.id));
  }

  personaProgress(persona: Audience): number {
    const personato = this.personaeWaitingRoom.find(p => p.id == persona.id);
    if (!personato) {
      this.logger.debug("Cannot find persona");
      return 0;
    }

    const target = personato;
    const bench = personato.benchmark;

    const targetStatus = target.status;
    const benchStatus = bench.status;

    const targetPercent = CrawlTrackerService.getPercentage(targetStatus);
    const benchPercent = CrawlTrackerService.getPercentage(benchStatus);

    const average = (targetPercent + benchPercent) / 2;
    return Math.round(average * 100);
  }

  progress(project: Project): number {
    const projecto = this.waitingRoom.find(p => p.id == project.id);
    if (!projecto) return 0;

    const target = projecto.audience_target;
    const bench = projecto.audience_bench;

    const targetStatus = target.status;
    const benchStatus = bench.status;

    // Projects tracked through the new (binary) crawler only expose a
    // coarse 'in_progress'/'done' signal (see ApiDataService.getProjectCrawlStatus).
    // Their audience_target/audience_bench status is only populated once the
    // crawl completes (see markAudiencesCrawlDone), so it is legitimately
    // null while such a project sits in the waiting room. Report "no progress
    // info yet" instead of dereferencing a null status, consistent with
    // getPercentage()'s own null handling below.
    if (!targetStatus || !benchStatus) return 0;

    let targetPercent;
    let benchPercent;

    if (targetStatus.actualStep == CrawlTrackerService.STEP_PERSONAE && benchStatus.actualStep < CrawlTrackerService.STEP_PERSONAE) {
      targetPercent = 1;
    } else {
      targetPercent = CrawlTrackerService.getPercentage(targetStatus);
    }

    if (benchStatus.actualStep == CrawlTrackerService.STEP_PERSONAE && targetStatus.actualStep < CrawlTrackerService.STEP_PERSONAE) {
      benchPercent = 1;
    } else {
      benchPercent = CrawlTrackerService.getPercentage(benchStatus);
    }

    let average = (targetPercent + benchPercent) / 2;
    average = Math.round(average * 100);

    if (average == 100 && (targetStatus.progress != targetStatus.size || benchStatus.progress != benchStatus.size)) {
      average--;
    }

    return average;
  }

  private static getPercentage(status: CrawlStatus): number {
    let percentage = 0;

    if (!status) return 0;
    else if (status.state == "done") return 1;
    else if (status.size == 0) return 0;
    else if (status.actualStep < this.STEP_PERSONAE) {
      const adjustedSteps = status.steps - 1; // Adjusted steps to not include persona crawl
      percentage = ((status.actualStep / adjustedSteps) - 1 / adjustedSteps) + (status.progress / status.size) * (1 / adjustedSteps);
    } else if (status.actualStep == this.STEP_PERSONAE) {
      percentage = status.progress / status.size;
    }

    return percentage;
  }
}
